import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { get, post, put, del } from '@/services/ApiService';
import {
    GET_SHOP_PRIVACY_POLICY,
    CREATE_SHOP_PRIVACY_POLICY,
    UPDATE_SHOP_PRIVACY_POLICY,
    DELETE_SHOP_PRIVACY_POLICY,
    ADD_SHOP_PRIVACY_SECTION,
    UPDATE_SHOP_PRIVACY_SECTION,
    DELETE_SHOP_PRIVACY_SECTION,
    ADD_SHOP_PRIVACY_SUB_SECTION,
    UPDATE_SHOP_PRIVACY_SUB_SECTION,
    DELETE_SHOP_PRIVACY_SUB_SECTION,
} from '@/services/ApiRoutes';

export interface SubSection {
    id?: string;
    _id?: string;
    title: string;
    content: string;
}

export interface Section {
    id?: string;
    _id?: string;
    title: string;
    order: number;
    subSections: SubSection[];
}

export interface ShopPrivacy {
    _id?: string;
    id?: string;
    title: string;
    content: string;
    lastUpdated?: string;
    sections?: Section[];
    tableOfContents?: string[];
    createdAt?: string;
    updatedAt?: string;
}

interface ShopPrivacyState {
    data: ShopPrivacy | null;
    loading: boolean;
    error: string | null;
}

const initialState: ShopPrivacyState = {
    data: null,
    loading: false,
    error: null,
};

export const fetchShopPrivacyPolicy = createAsyncThunk(
    'shopPrivacy/fetch',
    async (_, { rejectWithValue }) => {
        try {
            const response = await get(GET_SHOP_PRIVACY_POLICY);
            return response.data?.data || response.data || null;
        } catch (error: any) {
            if (error.response?.status === 404 || error.status === 404 || error.message?.toLowerCase().includes('not found')) {
                return null;
            }
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch shop privacy policy');
        }
    }
);

export const createShopPrivacyPolicy = createAsyncThunk(
    'shopPrivacy/create',
    async (data: { title: string; content?: string; sections?: Section[] }, { rejectWithValue }) => {
        try {
            const response = await post(CREATE_SHOP_PRIVACY_POLICY, data);
            return response.data?.data || response.data || response;
        } catch (error: any) {
            if (error.response?.status === 400 || error.response?.status === 409) {
                try {
                    const putRes = await put(UPDATE_SHOP_PRIVACY_POLICY, data);
                    return putRes.data?.data || putRes.data || putRes;
                } catch (putErr: any) {
                    return rejectWithValue(putErr.response?.data?.message || 'Failed to save shop privacy policy');
                }
            }
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create shop privacy policy');
        }
    }
);

export const updateShopPrivacyPolicy = createAsyncThunk(
    'shopPrivacy/update',
    async (data: { title?: string; content?: string; sections?: Section[] }, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_SHOP_PRIVACY_POLICY, data);
            return response.data?.data || response.data || response;
        } catch (error: any) {
            if (error.response?.status === 404 || error.response?.status === 400) {
                try {
                    const postRes = await post(CREATE_SHOP_PRIVACY_POLICY, data as any);
                    return postRes.data?.data || postRes.data || postRes;
                } catch (postErr: any) {
                    return rejectWithValue(postErr.response?.data?.message || 'Failed to update shop privacy policy');
                }
            }
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update shop privacy policy');
        }
    }
);

export const deleteShopPrivacyPolicy = createAsyncThunk(
    'shopPrivacy/delete',
    async (_, { rejectWithValue }) => {
        try {
            await del(DELETE_SHOP_PRIVACY_POLICY);
            return null;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete shop privacy policy');
        }
    }
);

export const addShopPrivacySection = createAsyncThunk(
    'shopPrivacy/addSection',
    async (data: { title: string; order?: number; subSections?: SubSection[] }, { rejectWithValue }) => {
        try {
            const response = await post(ADD_SHOP_PRIVACY_SECTION, data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add section');
        }
    }
);

export const updateShopPrivacySection = createAsyncThunk(
    'shopPrivacy/updateSection',
    async ({ sectionId, data }: { sectionId: string; data: { title?: string; order?: number } }, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_SHOP_PRIVACY_SECTION(sectionId), data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update section');
        }
    }
);

export const deleteShopPrivacySection = createAsyncThunk(
    'shopPrivacy/deleteSection',
    async (sectionId: string, { rejectWithValue }) => {
        try {
            const response = await del(DELETE_SHOP_PRIVACY_SECTION(sectionId));
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete section');
        }
    }
);

export const addShopPrivacySubSection = createAsyncThunk(
    'shopPrivacy/addSubSection',
    async ({ sectionId, data }: { sectionId: string; data: { title: string; content: string } }, { rejectWithValue }) => {
        try {
            const response = await post(ADD_SHOP_PRIVACY_SUB_SECTION(sectionId), data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to add sub-section');
        }
    }
);

export const updateShopPrivacySubSection = createAsyncThunk(
    'shopPrivacy/updateSubSection',
    async ({ sectionId, subId, data }: { sectionId: string; subId: string; data: { title?: string; content?: string } }, { rejectWithValue }) => {
        try {
            const response = await put(UPDATE_SHOP_PRIVACY_SUB_SECTION(sectionId, subId), data);
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update sub-section');
        }
    }
);

export const deleteShopPrivacySubSection = createAsyncThunk(
    'shopPrivacy/deleteSubSection',
    async ({ sectionId, subId }: { sectionId: string; subId: string }, { rejectWithValue }) => {
        try {
            const response = await del(DELETE_SHOP_PRIVACY_SUB_SECTION(sectionId, subId));
            return response.data?.data || response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete sub-section');
        }
    }
);

const shopPrivacySlice = createSlice({
    name: 'shopPrivacy',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchShopPrivacyPolicy.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchShopPrivacyPolicy.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchShopPrivacyPolicy.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(createShopPrivacyPolicy.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createShopPrivacyPolicy.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(createShopPrivacyPolicy.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(updateShopPrivacyPolicy.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateShopPrivacyPolicy.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(updateShopPrivacyPolicy.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(deleteShopPrivacyPolicy.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteShopPrivacyPolicy.fulfilled, (state) => {
                state.loading = false;
                state.data = null;
            })
            .addCase(deleteShopPrivacyPolicy.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Section & Sub-Section reducers
            .addCase(addShopPrivacySection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(updateShopPrivacySection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(deleteShopPrivacySection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(addShopPrivacySubSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(updateShopPrivacySubSection.fulfilled, (state, action) => {
                state.data = action.payload;
            })
            .addCase(deleteShopPrivacySubSection.fulfilled, (state, action) => {
                state.data = action.payload;
            });
    },
});

export const { clearError } = shopPrivacySlice.actions;
export default shopPrivacySlice.reducer;
